import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  Alert,
  CircularProgress,
  Link as MuiLink
} from '@mui/material';
import { authService } from '../../services/authService';

const AuthContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #275AA6 0%, #1e4a8c 100%);
`;

const AuthCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-width: 400px;
  width: 100%;
  margin: 20px;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 30px;
  
  h1 {
    color: #275AA6;
    font-weight: bold;
    margin: 0;
    font-size: 28px;
  }
  
  p {
    color: #666;
    margin: 5px 0 0 0;
    font-size: 14px;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const AuthForm = ({ onAuthSuccess }) => {
  const [formMode, setFormMode] = useState('signin'); // 'signin', 'signup', 'confirm', 'forgotpassword', 'resetpassword'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    verificationCode: '',
    name: {formatted:''},
    newPassword: '',
    confirmNewPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name==="name"?{"formatted":value}:value
    }));
    setError('');
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await authService.signIn(formData.email, formData.password);
    if (result.success) {
      onAuthSuccess(result.user);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    const result = await authService.signUp(formData.email, formData.password, formData.name);
    
    if (result.success) {
      setFormMode('confirm');
      setMessage('Please check your email for the verification code');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await authService.confirmSignUp(formData.email, formData.verificationCode);
    
    if (result.success) {
      setMessage('Account verified successfully! Please sign in.');
      setFormMode('signin');
      setFormData(prev => ({ ...prev, verificationCode: '' }));
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleResendCode = async () => {
    setLoading(true);
    const result = await authService.resendSignUpCode(formData.email);
    
    if (result.success) {
      setMessage('Verification code sent to your email');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await authService.resetPassword(formData.email);
    
    if (result.success) {
      setFormMode('resetpassword');
      setMessage('Password reset code sent to your email');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.newPassword !== formData.confirmNewPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    const result = await authService.confirmResetPassword(
      formData.email, 
      formData.verificationCode, 
      formData.newPassword
    );
    
    if (result.success) {
      setMessage('Password reset successful! Please sign in with your new password.');
      setFormMode('signin');
      setFormData(prev => ({ 
        ...prev, 
        verificationCode: '', 
        newPassword: '', 
        confirmNewPassword: '' 
      }));
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleContinueAsGuest = () => {
    window.location.href = '/guest';
  };

  const renderSignInForm = () => (
    <StyledForm onSubmit={handleSignIn}>
      <TextField
        name="email"
        type="email"
        label="Email Address"
        value={formData.email}
        onChange={handleInputChange}
        required
        fullWidth
        variant="outlined"
      />
      <TextField
        name="password"
        type="password"
        label="Password"
        value={formData.password}
        onChange={handleInputChange}
        required
        fullWidth
        variant="outlined"
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{ 
          backgroundColor: '#275AA6',
          '&:hover': { backgroundColor: '#1e4a8c' },
          py: 1.5,
          fontSize: '16px'
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
      </Button>
      <Box textAlign="center">
        <Typography variant="body2" sx={{ mb: 1 }}>
          <MuiLink
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              setFormMode('forgotpassword');
              setError('');
              setMessage('');
            }}
            sx={{ color: '#275AA6' }}
          >
            Forgot Password?
          </MuiLink>
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Don't have an account?{' '}
          <MuiLink
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              setFormMode('signup');
              setError('');
              setMessage('');
            }}
            sx={{ color: '#275AA6' }}
          >
            Sign Up
          </MuiLink>
        </Typography>
        <Button
          onClick={handleContinueAsGuest}
          variant="outlined"
          fullWidth
          sx={{ 
            borderColor: '#275AA6',
            color: '#275AA6',
            '&:hover': { 
              borderColor: '#1e4a8c',
              backgroundColor: 'rgba(39, 90, 166, 0.04)'
            },
            py: 1.5,
            fontSize: '16px'
          }}
        >
          Continue as Guest
        </Button>
      </Box>
    </StyledForm>
  );

  const renderSignUpForm = () => (
    <StyledForm onSubmit={handleSignUp}>
      <TextField
        name="name"
        type="text"
        label="Full Name"
        value={formData.name.formatted}
        onChange={handleInputChange}
        fullWidth
        variant="outlined"
      />
      <TextField
        name="email"
        type="email"
        label="Email Address"
        value={formData.email}
        onChange={handleInputChange}
        required
        fullWidth
        variant="outlined"
      />
      <TextField
        name="password"
        type="password"
        label="Password"
        value={formData.password}
        onChange={handleInputChange}
        required
        fullWidth
        variant="outlined"
        helperText="Must be at least 8 characters with uppercase, lowercase, numbers, and special characters"
      />
      <TextField
        name="confirmPassword"
        type="password"
        label="Confirm Password"
        value={formData.confirmPassword}
        onChange={handleInputChange}
        required
        fullWidth
        variant="outlined"
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{ 
          backgroundColor: '#275AA6',
          '&:hover': { backgroundColor: '#1e4a8c' },
          py: 1.5,
          fontSize: '16px'
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
      </Button>
      <Box textAlign="center">
        <Typography variant="body2" sx={{ mb: 2 }}>
          Already have an account?{' '}
          <MuiLink
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              setFormMode('signin');
              setError('');
              setMessage('');
            }}
            sx={{ color: '#275AA6' }}
          >
            Sign In
          </MuiLink>
        </Typography>
        <Button
          onClick={handleContinueAsGuest}
          variant="outlined"
          fullWidth
          sx={{ 
            borderColor: '#275AA6',
            color: '#275AA6',
            '&:hover': { 
              borderColor: '#1e4a8c',
              backgroundColor: 'rgba(39, 90, 166, 0.04)'
            },
            py: 1.5,
            fontSize: '16px'
          }}
        >
          Continue as Guest
        </Button>
      </Box>
    </StyledForm>
  );

  const renderConfirmForm = () => (
    <StyledForm onSubmit={handleConfirmSignUp}>
      <Typography variant="body1" textAlign="center" color="text.secondary">
        Enter the verification code sent to {formData.email}
      </Typography>
      <TextField
        name="verificationCode"
        label="Verification Code"
        value={formData.verificationCode}
        onChange={handleInputChange}
        required
        fullWidth
        variant="outlined"
        placeholder="Enter 6-digit code"
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{ 
          backgroundColor: '#275AA6',
          '&:hover': { backgroundColor: '#1e4a8c' },
          py: 1.5,
          fontSize: '16px'
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Account'}
      </Button>
      <Box textAlign="center">
        <Typography variant="body2">
          Didn't receive the code?{' '}
          <MuiLink
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              handleResendCode();
            }}
            sx={{ color: '#275AA6' }}
            disabled={loading}
          >
            Resend Code
          </MuiLink>
        </Typography>
      </Box>
      <Box textAlign="center">
        <Typography variant="body2">
          <MuiLink
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              setFormMode('signin');
              setError('');
              setMessage('');
            }}
            sx={{ color: '#275AA6' }}
          >
            Back to Sign In
          </MuiLink>
        </Typography>
      </Box>
    </StyledForm>
  );

  const renderForgotPasswordForm = () => (
    <StyledForm onSubmit={handleForgotPassword}>
      <Typography variant="body1" textAlign="center" color="text.secondary">
        Enter your email address to receive a password reset code
      </Typography>
      <TextField
        name="email"
        type="email"
        label="Email Address"
        value={formData.email}
        onChange={handleInputChange}
        required
        fullWidth
        variant="outlined"
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{ 
          backgroundColor: '#275AA6',
          '&:hover': { backgroundColor: '#1e4a8c' },
          py: 1.5,
          fontSize: '16px'
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Code'}
      </Button>
      <Box textAlign="center">
        <Typography variant="body2">
          Remember your password?{' '}
          <MuiLink
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              setFormMode('signin');
              setError('');
              setMessage('');
            }}
            sx={{ color: '#275AA6' }}
          >
            Back to Sign In
          </MuiLink>
        </Typography>
      </Box>
    </StyledForm>
  );

  const renderResetPasswordForm = () => (
    <StyledForm onSubmit={handleResetPassword}>
      <Typography variant="body1" textAlign="center" color="text.secondary">
        Enter the verification code sent to {formData.email} and your new password
      </Typography>
      <TextField
        name="verificationCode"
        label="Verification Code"
        value={formData.verificationCode}
        onChange={handleInputChange}
        required
        fullWidth
        variant="outlined"
        placeholder="Enter 6-digit code"
      />
      <TextField
        name="newPassword"
        type="password"
        label="New Password"
        value={formData.newPassword}
        onChange={handleInputChange}
        required
        fullWidth
        variant="outlined"
        helperText="Must be at least 8 characters with uppercase, lowercase, numbers, and special characters"
      />
      <TextField
        name="confirmNewPassword"
        type="password"
        label="Confirm New Password"
        value={formData.confirmNewPassword}
        onChange={handleInputChange}
        required
        fullWidth
        variant="outlined"
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{ 
          backgroundColor: '#275AA6',
          '&:hover': { backgroundColor: '#1e4a8c' },
          py: 1.5,
          fontSize: '16px'
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
      </Button>
      <Box textAlign="center">
        <Typography variant="body2">
          <MuiLink
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              setFormMode('signin');
              setError('');
              setMessage('');
            }}
            sx={{ color: '#275AA6' }}
          >
            Back to Sign In
          </MuiLink>
        </Typography>
      </Box>
    </StyledForm>
  );

  return (
    <AuthContainer>
      <AuthCard>
        <Logo>
          <img 
            src="https://web-app-support.s3.us-east-2.amazonaws.com/AviationSafetyMonitorLogo.png" 
            alt="Aviation Safety Monitor Logo"
            style={{ maxWidth: '160px', maxHeight: '100px', objectFit: 'contain', marginBottom: '5px' }}
          />
          <h1>Risk Tracker</h1>
          <p>A service of the Aviation Safety Monitor</p>
        </Logo>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        {formMode === 'signin' && renderSignInForm()}
        {formMode === 'signup' && renderSignUpForm()}
        {formMode === 'confirm' && renderConfirmForm()}
        {formMode === 'forgotpassword' && renderForgotPasswordForm()}
        {formMode === 'resetpassword' && renderResetPasswordForm()}
      </AuthCard>
    </AuthContainer>
  );
};

export default AuthForm;